import { useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ShoppingBag } from "lucide-react";
import { scrollToHash } from "./scroll";
import goldenClean from "@/assets/golden_clean.png";
import pinkClean from "@/assets/pink_clean.png";
import fallClean from "@/assets/fall_clean.png";

const GUACCI = "https://farmaciaguacci.it/brahma-golden-pb-50ml";
const EASE = [0.22, 1, 0.36, 1];

const Line = ({ children, delay = 0, className = "" }) => (
  <span className="block overflow-hidden">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "112%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.15, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const ORBIT = [
  {
    src: goldenClean,
    alt: "Face Cream Brahma Golden PB",
    hCls: "bottle-h-golden",
    pad: "-9px",
    testid: "hero-product-golden-pb",
    anchor: "#golden-pb",
    label: "Golden PB",
    labelCls: "text-gold-deep",
  },
  {
    src: fallClean,
    alt: "Cleansing Oil Brahma Fall Water",
    hCls: "bottle-h-fall",
    pad: "-8px",
    testid: "hero-product-fall-water",
    anchor: "#fall-water",
    label: "Fall Water",
    labelCls: "text-sage-deep",
  },
  {
    src: pinkClean,
    alt: "Face Serum Brahma Pink Lady",
    hCls: "bottle-h-pink",
    pad: "-7px",
    testid: "hero-product-pink-lady",
    anchor: "#pink-lady",
    label: "Pink Lady",
    labelCls: "text-blush-deep",
  },
];

const Pedestal = () => (
  <svg viewBox="0 0 520 210" className="absolute bottom-0 left-0 w-full" aria-hidden="true">
    <defs>
      <pattern id="marbleWall" width="280" height="210" patternUnits="userSpaceOnUse">
        <rect width="280" height="210" fill="#F2EEE5" />
        <rect x="70" width="80" height="210" fill="#E6E0D1" />
        <rect x="150" width="70" height="210" fill="#EFEAE0" />
        <rect x="220" width="60" height="210" fill="#E9E3D3" />
      </pattern>
      <pattern id="marbleTop" width="280" height="280" patternUnits="userSpaceOnUse">
        <rect width="280" height="280" fill="#F5F1E9" />
        <rect x="0" width="90" height="280" fill="#EAE4D5" />
        <rect x="130" width="60" height="280" fill="#F0EBE1" />
        <rect x="190" width="55" height="280" fill="#E4DCCB" />
      </pattern>
      <linearGradient id="wallLight" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0" stopColor="#FFFFFF" stopOpacity="0.5" />
        <stop offset="0.3" stopColor="#FFFFFF" stopOpacity="0" />
        <stop offset="0.75" stopColor="#1C1C1E" stopOpacity="0.05" />
        <stop offset="1" stopColor="#1C1C1E" stopOpacity="0.16" />
      </linearGradient>
      <radialGradient id="topLight" cx="0.3" cy="0.25" r="0.9">
        <stop offset="0" stopColor="#FFFFFF" stopOpacity="0.85" />
        <stop offset="0.55" stopColor="#FFF6E6" stopOpacity="0.25" />
        <stop offset="0.8" stopColor="#FFFFFF" stopOpacity="0" />
      </radialGradient>
      <radialGradient id="contactShadow" cx="0.5" cy="0.5" r="0.5">
        <stop offset="0" stopColor="#1C1C1E" stopOpacity="0.18" />
        <stop offset="0.7" stopColor="#1C1C1E" stopOpacity="0" />
      </radialGradient>
      <linearGradient id="ringGrad" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0" stopColor="#FFF8EC" stopOpacity="0.98" />
        <stop offset="0.5" stopColor="#F4DFC0" stopOpacity="0.9" />
        <stop offset="1" stopColor="#FFF3DE" stopOpacity="0.95" />
      </linearGradient>
      <filter id="ringGlow" x="-60%" y="-60%" width="220%" height="220%">
        <feGaussianBlur stdDeviation="7" result="b" />
        <feMerge>
          <feMergeNode in="b" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
      <filter id="ringGlowSoft" x="-80%" y="-80%" width="260%" height="260%">
        <feGaussianBlur stdDeviation="14" />
      </filter>
      <clipPath id="wallClip">
        <path d="M16 64 A244 56 0 0 0 504 64 L504 158 A244 52 0 0 1 16 158 Z" />
      </clipPath>
      <clipPath id="topClip">
        <ellipse cx="260" cy="64" rx="244" ry="56" />
      </clipPath>
    </defs>

    <g clipPath="url(#wallClip)">
      <g className="wall-scroll-svg">
        <rect x="-280" y="0" width="1120" height="210" fill="url(#marbleWall)" />
      </g>
      <rect x="0" y="0" width="520" height="210" fill="url(#wallLight)" />
    </g>

    <ellipse cx="260" cy="158" rx="244" ry="52" fill="#E9E3D6" />
    <ellipse cx="260" cy="154" rx="244" ry="52" fill="#F3EFE6" />
    <ellipse cx="260" cy="154" rx="244" ry="52" fill="url(#wallLight)" />

    <g clipPath="url(#topClip)">
      <g className="plate-spin-svg" style={{ transformOrigin: "260px 64px", transformBox: "view-box" }}>
        <rect x="-280" y="-216" width="1120" height="560" fill="url(#marbleTop)" />
      </g>
      <ellipse cx="260" cy="84" rx="150" ry="26" fill="url(#contactShadow)" />
      <ellipse cx="260" cy="64" rx="244" ry="56" fill="url(#topLight)" />
      <g filter="url(#ringGlowSoft)">
        <ellipse
          cx="260"
          cy="66"
          rx="200"
          ry="43"
          fill="none"
          stroke="#F4DFC0"
          strokeWidth="10"
          opacity="0.4"
        />
      </g>
      <g filter="url(#ringGlow)">
        <ellipse
          cx="260"
          cy="66"
          rx="200"
          ry="43"
          fill="none"
          stroke="url(#ringGrad)"
          strokeWidth="5"
          opacity="1"
        />
      </g>
    </g>
    <ellipse cx="260" cy="64" rx="244" ry="56" fill="none" stroke="rgba(197,160,89,0.4)" />
    <ellipse cx="260" cy="64" rx="222" ry="47" fill="none" stroke="rgba(255,255,255,0.75)" />
  </svg>
);

export const Hero = () => {
  const ref = useRef(null);
  const sceneRef = useRef(null);
  const bottleRefs = useRef([]);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 110]);

  useEffect(() => {
    let raf;
    let last = performance.now();
    let phase = Math.PI / 2;
    const tick = (t) => {
      const scene = sceneRef.current;
      if (scene) {
        const dt = Math.min(0.05, (t - last) / 1000);
        last = t;
        if (!scene.classList.contains("orbit-paused")) {
          phase += (dt * 2 * Math.PI) / 24;
        }
        const R = scene.offsetWidth * 0.26;
        const Ey = scene.offsetWidth * 0.04;
        bottleRefs.current.forEach((el, i) => {
          if (!el) return;
          const theta = phase + (i * 2 * Math.PI) / 3;
          const x = Math.cos(theta) * R;
          const z = Math.sin(theta);
          const s = 0.86 + 0.14 * z;
          el.style.transform = `translateX(calc(-50% + ${x.toFixed(1)}px)) translateY(${(
            z * Ey
          ).toFixed(1)}px) scale(${s.toFixed(3)})`;
          el.style.transformOrigin = "50% 100%";
          // keep bottles fully opaque: semi-transparency let the gold glow tint the white bodies
          el.style.opacity = "1";
          el.style.zIndex = z > 0 ? 30 : 10;
        });
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  const pauseOrbit = () => sceneRef.current?.classList.add("orbit-paused");
  const resumeOrbit = () => sceneRef.current?.classList.remove("orbit-paused");

  return (
    <section
      id="top"
      ref={ref}
      className="marble-bg grain relative flex min-h-[100svh] items-center overflow-x-clip"
    >
      <div className="pointer-events-none absolute -left-32 top-24 h-96 w-96 rounded-full bg-blush/70 blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 left-1/3 h-80 w-80 rounded-full bg-sage/60 blur-3xl" />
      <div className="pointer-events-none absolute right-1/4 top-16 h-72 w-72 rounded-full bg-gold-pale/80 blur-3xl" />

      <div className="relative z-10 mx-auto grid w-full max-w-7xl items-center gap-14 px-5 pb-20 pt-32 lg:grid-cols-[1.05fr_0.95fr] lg:px-10 lg:pt-28">
        <div>
          <Line delay={0.1}>
            <span className="text-[11px] font-medium uppercase tracking-[0.42em] text-gold-deep">
              La nostra filosofia
            </span>
          </Line>
          <h1
            data-testid="hero-title"
            className="mt-6 font-display text-[clamp(4rem,11vw,8.5rem)] font-light leading-[0.95] text-ink"
          >
            <Line delay={0.25}>
              <span className="tracking-[0.2em]">Brahma</span>
            </Line>
          </h1>
          <Line delay={0.45}>
            <span className="mt-3 block font-display text-2xl italic text-gold-deep lg:text-3xl">
              by Pómalab
            </span>
          </Line>
          <Line delay={0.6}>
            <p className="mt-8 max-w-md text-base font-light leading-relaxed text-smoke lg:text-lg">
              L'unione di efficacia e delicatezza della tecnologia postbiotica e naturale Brahma per
              rigenerare una pelle sana e protetta.
            </p>
          </Line>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.85, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#prodotti"
              onClick={scrollToHash("#prodotti")}
              data-testid="hero-cta-products"
              className="group flex items-center gap-2 rounded-full bg-ink px-7 py-3.5 text-[11px] uppercase tracking-[0.22em] text-ivory transition-all duration-300 hover:bg-gold-deep"
            >
              Scopri la linea
              <ArrowDown className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-y-0.5" />
            </a>
            <a
              href={GUACCI}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-buy"
              className="flex items-center gap-2 rounded-full border border-gold/50 bg-white/60 px-7 py-3.5 text-[11px] uppercase tracking-[0.22em] text-gold-deep transition-all duration-300 hover:border-gold hover:bg-gold-pale"
            >
              <ShoppingBag className="h-3.5 w-3.5" />
              Acquista ora
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.1 }}
            className="mt-10 flex flex-wrap gap-2"
          >
            {["Formulazione unisex", "Filtri UVA-UVB", "Rituale in tre gesti"].map((b) => (
              <span
                key={b}
                className="rounded-full border border-gold/30 bg-white/70 px-4 py-1.5 text-[10px] uppercase tracking-[0.22em] text-smoke"
              >
                {b}
              </span>
            ))}
          </motion.div>
        </div>

        <motion.div
          style={{ y: yImg }}
          className="relative mx-auto w-full max-w-[420px] sm:max-w-[480px] lg:max-w-[540px]"
        >
          <div
            className="pointer-events-none absolute left-1/2 top-[42%] aspect-square w-[115%] -translate-x-1/2 -translate-y-1/2 rounded-full"
            style={{
              border: "3px solid #FFF2D6",
              boxShadow:
                "0 0 35px 12px rgba(245, 208, 140, 0.75), inset 0 0 20px rgba(255, 230, 180, 0.6)",
              filter: "drop-shadow(0 0 20px #E6BC70)",
            }}
            aria-hidden="true"
          />
          <div className="pointer-events-none absolute left-1/2 top-[42%] h-[80%] w-[92%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-gold-pale/60 blur-3xl" />

          <div ref={sceneRef} className="relative mx-auto w-full" style={{ paddingBottom: "112%" }}>
            <div
              className="absolute inset-x-[10%] bottom-[2%] h-[4%] rounded-[50%] bg-black/5 blur-xl"
              aria-hidden="true"
            />
            <Pedestal />
            {ORBIT.map((o, i) => (
              <div
                key={o.label}
                ref={(el) => (bottleRefs.current[i] = el)}
                className="absolute bottom-[25%] left-1/2"
                style={{ transform: "translateX(-50%)" }}
                onPointerEnter={pauseOrbit}
                onPointerLeave={resumeOrbit}
              >
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 1.1, delay: 0.5 + i * 0.15, ease: EASE }}
                  className="relative"
                >
                  <a
                    href={o.anchor}
                    onClick={scrollToHash(o.anchor)}
                    className="bottle-click"
                    data-testid={`hero-bottle-${o.label.toLowerCase().replace(" ", "-")}`}
                  >
                    <div className="relative flex flex-col bottle-hover-fx">
                      <span aria-hidden="true" className="bottle-contact-soft" />
                      <span aria-hidden="true" className="bottle-contact-core" />
                      <div className="relative" style={{ marginBottom: o.pad }}>
                        <img
                          src={o.src}
                          alt={o.alt}
                          data-testid={o.testid}
                          className={`relative block ${o.hCls}`}
                          style={{
                            filter: "drop-shadow(0 0 14px rgba(245, 215, 150, 0.45))",
                          }}
                        />
                        <span
                          aria-hidden="true"
                          className="bottle-satin"
                          style={{ WebkitMaskImage: `url(${o.src})`, maskImage: `url(${o.src})` }}
                        />
                      </div>
                      <div
                        aria-hidden="true"
                        className="pointer-events-none absolute left-0 top-full w-full overflow-hidden"
                        style={{
                          zIndex: -1,
                          height: "34%",
                          maskImage:
                            "linear-gradient(to bottom, rgba(0,0,0,0.4) 0%, transparent 65%)",
                          WebkitMaskImage:
                            "linear-gradient(to bottom, rgba(0,0,0,0.4) 0%, transparent 65%)",
                        }}
                      >
                        <img
                          src={o.src}
                          alt=""
                          className="block w-full"
                          style={{
                            transform: "scaleY(-1)",
                            marginTop: o.pad,
                            opacity: 0.14,
                            filter: "blur(1.5px)",
                          }}
                        />
                      </div>
                    </div>
                  </a>
                </motion.div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
